import { type BinaryOp, type Expr, ExpressionError, type RefScope } from "./ast";

/**
 * Sichere Ausdruckssprache für Berechnungen und Variablen. Kein `eval`, keine Schleifen,
 * keine Zuweisungen, kein Zugriff auf JS-Objekte.
 *
 * Syntax:
 *   Zahlen      12, 3.5, 1e3
 *   Texte       "Hallo" oder 'Hallo'
 *   Literale    true, false, null
 *   Referenzen  answers.<key>, vars.<name>, hidden.<name>
 *   Operatoren  + - * / %   == != < <= > >=   && || !   bedingung ? a : b
 *   Funktionen  siehe FUNCTIONS in evaluate.ts
 */

const MAX_LENGTH = 2000;
const MAX_DEPTH = 64;
const SCOPES: ReadonlySet<string> = new Set(["answers", "vars", "hidden"]);

type Token =
  | { t: "num"; v: number; pos: number }
  | { t: "str"; v: string; pos: number }
  | { t: "ident"; v: string; pos: number }
  | { t: "op"; v: string; pos: number }
  | { t: "eof"; pos: number };

const OPERATORS = ["&&", "||", "==", "!=", "<=", ">=", "+", "-", "*", "/", "%", "<", ">", "!", "(", ")", ",", "?", ":", "."];

function tokenize(src: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < src.length) {
    const ch = src[i]!;
    if (/\s/.test(ch)) {
      i++;
      continue;
    }
    if (/[0-9]/.test(ch) || (ch === "." && /[0-9]/.test(src[i + 1] ?? ""))) {
      const m = /^(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/.exec(src.slice(i))!;
      tokens.push({ t: "num", v: Number(m[0]), pos: i });
      i += m[0].length;
      continue;
    }
    if (ch === '"' || ch === "'") {
      const quote = ch;
      let j = i + 1;
      let out = "";
      while (j < src.length && src[j] !== quote) {
        if (src[j] === "\\" && j + 1 < src.length) {
          const next = src[j + 1]!;
          out += next === "n" ? "\n" : next === "t" ? "\t" : next;
          j += 2;
        } else {
          out += src[j];
          j++;
        }
      }
      if (j >= src.length) throw new ExpressionError("Text nicht abgeschlossen", i);
      tokens.push({ t: "str", v: out, pos: i });
      i = j + 1;
      continue;
    }
    if (/[A-Za-z_]/.test(ch)) {
      const m = /^[A-Za-z_][A-Za-z0-9_]*/.exec(src.slice(i))!;
      tokens.push({ t: "ident", v: m[0], pos: i });
      i += m[0].length;
      continue;
    }
    const op = OPERATORS.find((o) => src.startsWith(o, i));
    if (!op) throw new ExpressionError(`Unerwartetes Zeichen „${ch}“`, i);
    tokens.push({ t: "op", v: op, pos: i });
    i += op.length;
  }
  tokens.push({ t: "eof", pos: src.length });
  return tokens;
}

const BINARY_PRECEDENCE: Record<string, number> = {
  "||": 1,
  "&&": 2,
  "==": 3,
  "!=": 3,
  "<": 4,
  "<=": 4,
  ">": 4,
  ">=": 4,
  "+": 5,
  "-": 5,
  "*": 6,
  "/": 6,
  "%": 6,
};

export function parseExpression(src: string): Expr {
  if (src.length > MAX_LENGTH) throw new ExpressionError(`Ausdruck zu lang (max. ${MAX_LENGTH} Zeichen)`);
  const tokens = tokenize(src);
  let p = 0;
  let depth = 0;

  const peek = () => tokens[p]!;
  const next = () => tokens[p++]!;
  const isOp = (v: string) => {
    const tk = peek();
    return tk.t === "op" && tk.v === v;
  };
  const expectOp = (v: string) => {
    const tk = next();
    if (tk.t !== "op" || tk.v !== v) throw new ExpressionError(`„${v}“ erwartet`, tk.pos);
  };
  const enter = () => {
    if (++depth > MAX_DEPTH) throw new ExpressionError("Ausdruck zu tief verschachtelt");
  };

  function parseConditional(): Expr {
    enter();
    const test = parseBinary(0);
    if (isOp("?")) {
      next();
      const thenExpr = parseConditional();
      expectOp(":");
      const elseExpr = parseConditional();
      depth--;
      return { kind: "conditional", test, then: thenExpr, else: elseExpr };
    }
    depth--;
    return test;
  }

  function parseBinary(minPrec: number): Expr {
    let left = parseUnary();
    for (;;) {
      const tk = peek();
      if (tk.t !== "op") break;
      const prec = BINARY_PRECEDENCE[tk.v];
      if (prec === undefined || prec <= minPrec) break;
      next();
      const right = parseBinary(prec);
      left = { kind: "binary", op: tk.v as BinaryOp, left, right };
    }
    return left;
  }

  function parseUnary(): Expr {
    if (isOp("-") || isOp("!")) {
      const op = next() as { v: "-" | "!" };
      enter();
      const arg = parseUnary();
      depth--;
      return { kind: "unary", op: op.v, arg };
    }
    return parsePrimary();
  }

  function parsePrimary(): Expr {
    const tk = next();
    switch (tk.t) {
      case "num":
        return { kind: "literal", value: tk.v };
      case "str":
        return { kind: "literal", value: tk.v };
      case "op":
        if (tk.v === "(") {
          const e = parseConditional();
          expectOp(")");
          return e;
        }
        throw new ExpressionError(`Unerwartetes „${tk.v}“`, tk.pos);
      case "ident": {
        if (tk.v === "true") return { kind: "literal", value: true };
        if (tk.v === "false") return { kind: "literal", value: false };
        if (tk.v === "null") return { kind: "literal", value: null };
        if (SCOPES.has(tk.v)) {
          expectOp(".");
          const name = next();
          if (name.t !== "ident") throw new ExpressionError("Name nach „.“ erwartet", name.pos);
          return { kind: "ref", scope: tk.v as RefScope, name: name.v };
        }
        if (isOp("(")) {
          next();
          const args: Expr[] = [];
          if (!isOp(")")) {
            do args.push(parseConditional());
            while (isOp(",") && next());
          }
          expectOp(")");
          return { kind: "call", name: tk.v, args };
        }
        throw new ExpressionError(
          `Unbekannter Name „${tk.v}“ – Referenzen beginnen mit answers., vars. oder hidden.`,
          tk.pos,
        );
      }
      case "eof":
        throw new ExpressionError("Ausdruck unvollständig", tk.pos);
    }
  }

  const ast = parseConditional();
  const rest = peek();
  if (rest.t !== "eof") throw new ExpressionError("Unerwarteter Rest im Ausdruck", rest.pos);
  return ast;
}

/** Alle Referenzen eines Ausdrucks (für Lint und Abhängigkeitsanalyse). */
export function collectRefs(expr: Expr, out: Array<{ scope: RefScope; name: string }> = []) {
  switch (expr.kind) {
    case "ref":
      out.push({ scope: expr.scope, name: expr.name });
      break;
    case "unary":
      collectRefs(expr.arg, out);
      break;
    case "binary":
      collectRefs(expr.left, out);
      collectRefs(expr.right, out);
      break;
    case "conditional":
      collectRefs(expr.test, out);
      collectRefs(expr.then, out);
      collectRefs(expr.else, out);
      break;
    case "call":
      expr.args.forEach((a) => collectRefs(a, out));
      break;
  }
  return out;
}

export function collectCalls(expr: Expr, out: string[] = []): string[] {
  if (expr.kind === "call") out.push(expr.name);
  if (expr.kind === "unary") collectCalls(expr.arg, out);
  if (expr.kind === "binary") (collectCalls(expr.left, out), collectCalls(expr.right, out));
  if (expr.kind === "conditional") (collectCalls(expr.test, out), collectCalls(expr.then, out), collectCalls(expr.else, out));
  if (expr.kind === "call") expr.args.forEach((a) => collectCalls(a, out));
  return out;
}
